import { useTerminalsState } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { trpc } from "@/Utils/trpc";
import { useNavigate } from "@/Utils/useNavigate";
import { useConfig } from "@/api/hooks/useConfig";
import useCustomerParents from "@/api/hooks/useCustomerParents";
import useCustomers from "@/api/hooks/useCustomers";
import { IImportContext } from "@/components/ImportComponents/ImportContext";
import { ImportView } from "@/components/ImportComponents/ImportView";
import { Template } from "@/components/ImportComponents/types";
import { Page } from "@/components/Page";
import { useAccessParentsWithPoints } from "@/pages/infrastructure/manage/useAccessParentsWithPoints";
import { useTrans } from "@/translations";

import React, { useCallback, useMemo } from "react";

import { addToSystem } from "./addToSystem";
import { getImportSteps } from "./importSteps";
import { getTemplates } from "./templates";

export const ImportTenants: React.FC = () => {
  const { t } = useTrans();
  const { data: chains } = useCustomerParents();
  const { tenantCategories } = useCommonEntitiesStore();
  const navigate = useNavigate();
  const { accessParents } = useAccessParentsWithPoints();
  const { refetchCustomers } = useCustomers();
  const { currentTerminal } = useTerminalsState();
  const { isMWM } = useConfig();

  const { data: allAccessParents } = trpc.accessParents.getAll.useQuery({
    terminalId: isMWM ? undefined : currentTerminal?.id,
  });

  const { mutateAsync: bulkCreateCustomers } =
    trpc.customers.bulkCreate.useMutation();

  const breadcrumbs = useMemo(
    () => [
      { url: "/customer/manage", name: t("sidebarLabels:manageCustomers") },
    ],
    [t],
  );

  const templates: Template[] = useMemo(
    () =>
      getTemplates({
        chains,
        tenantCategories: tenantCategories || [],
        isMWM,
        allAccessParents,
      }),
    [chains, tenantCategories, isMWM, allAccessParents],
  );

  const addToSystemFunction = useCallback(
    (context: IImportContext) =>
      addToSystem({
        context,
        accessParentIds: accessParents.map((el) => el.id),
        tenantCategories,
        bulkCreateCustomers,
        refetchCustomers,
        isMWM,
      }),
    [
      accessParents,
      bulkCreateCustomers,
      refetchCustomers,
      tenantCategories,
      isMWM,
    ],
  );

  const onCancel = useCallback(() => {
    navigate("/customer/manage");
  }, [navigate]);

  const importSteps = useMemo(() => getImportSteps({ isMWM }), [isMWM]);

  return (
    <Page
      title="importLabels:importCustomers"
      fullHeight
      breadcrumbsPaths={breadcrumbs}
    >
      <ImportView
        steps={importSteps}
        templates={templates}
        onCancel={onCancel}
        addToSystem={addToSystemFunction}
      />
    </Page>
  );
};
